import { useFormik } from "formik";
import { ConfirmPopup } from "primereact/confirmpopup";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit } from "react-icons/fa";
import { MdOutlineFlag, MdOutlineFolderOpen, MdOutlineSave } from "react-icons/md";
import { useParams } from "react-router-dom";
import * as Yup from "yup";

import { ReactComponent as PublishIcon } from "../../../../assets/publish.svg";
import { IDE_TYPE } from "../../../../constants/common";
import { useSnack } from "../../../../hooks/useSnack";
import lessonServices from "../../../../services/lessonServices";
import studentServices from "../../../../services/studentServices";
import Button from "../../../Button";
import ChallengeProjectGoalModal from "../../../modals/student/ChallengeProjectGoalModal";
import ConfirmPublishChallengeModal from "../../../modals/student/ConfirmPublishChallengeModal";
import ConfirmPublishGraduationModal from "../../../modals/student/ConfirmPublishGraduationModal";
import ConfirmPublishPlaygroundModal from "../../../modals/student/ConfirmPublishPlaygroundModal";
import MyProjectsModal from "../../../modals/student/MyProjectsModal";
import SuccessPublishChallengeModal from "../../../modals/student/SuccessPublishChallengeModal";
import SuccessPublishGraduationModal from "../../../modals/student/SuccessPublishGraduationModal";
import SuccessPublishPlaygroundModal from "../../../modals/student/SuccessPublishPlaygroundModal";
import PythonCodeEditor from "../../editors/pythonIDE/PythonCodeEditor";

const StudentPythonIDEWrapper = ({ onSubmit, ...rest }) => {
  const { t } = useTranslation();
  const { type = "playground", lessonID, difficulty } = useParams();
  const { showSnack } = useSnack();

  const [codeData, setCodeData] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [projectID, setProjectID] = useState(null);
  const [python, setPython] = useState("");

  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [projectName, setProjectName] = useState(t("untitled_project"));
  const [renameVisible, setRenameVisible] = useState(false);
  const [reRunCode, setReRunCode] = useState(false);
  const [submitProjectStatus, setSubmitProjectStatus] = useState(null);

  const isResetRef = useRef(false);
  const popupRef = useRef(null);
  const myProjectsModalRef = useRef();
  const challengeProjectGoalModalRef = useRef();
  const confirmPublishChallengeModalRef = useRef();
  const confirmPublishGraduationModalRef = useRef();
  const successPublishChallengeModalRef = useRef();
  const successPublishGraduationModalRef = useRef();
  const successPublishPlaygroundModalRef = useRef();
  const confirmPublishPlaygroundModalRef = useRef();

  const HandleConfirmPublishChallengeModalSuccess = () => {
    handlePublishChallenge(projectName);
  };
  const HandleConfirmPublishGraduationModalSuccess = () => {
    handlePublishProject(projectName, lessonID);
  };
  const HandleConfirmPublishPlaygroundModalSuccess = () => {
    handlePublishProject(projectName);
  };

  const HandleSuccessPublishChallengeModalSuccess = () => {};
  const HandleSuccessPublishGraduationModalSuccess = () => {};
  const HandleSuccessPublishPlaygroundModalSuccess = () => {};

  useEffect(() => {
    if (type === "playground" || type === "graduation" || type === "challenge") {
      if (!hasCodeContent()) return; // Don't save if empty

      const timeoutId = setTimeout(() => {
        if (type === "graduation" || type === "playground") {
          handleSaveProject(projectName, lessonID, true);
        } else {
          handleSaveChallenge(projectName, true);
        }
      }, 500); // 500 milliseconds debounce

      return () => clearTimeout(timeoutId); // Clear previous debounce on new keystroke
    }
  }, [python, projectName]);

  const submitEntity = async ({
    isAutoSave,
    entityType,
    status,
    payload,
    setSavingState, // setIsSaving or setIsPublishing or any other custom setter
    existingID, // projectID or challengeID
    serviceMethod, // studentServices.saveOrPublishStudentProjects or studentServices.saveOrPublishStudentChallenges
  }) => {
    try {
      if (!isAutoSave) setSavingState(true);

      let response;

      if (projectData?.id || existingID) {
        response = await serviceMethod(
          payload,
          projectData?.lesson_project_id || projectData?.id || existingID
        );
      } else {
        response = await serviceMethod(payload);
        setProjectID?.(response?.id); // Set ID only if applicable
      }
      if (status == "publish") {
        setSubmitProjectStatus(response);
      } else if (type === "code") {
        setSubmitProjectStatus(response);
      }

      if (isAutoSave) return;

      if (status === "save") {
        showSnack(t("saved_successfully"), "success");
        return;
      }

      if (entityType === "project") {
        if (lessonID) {
          // for graduation project
          confirmPublishGraduationModalRef.current?.closeModal();
          successPublishGraduationModalRef.current?.openModal();
        } else {
          // for playground project
          confirmPublishPlaygroundModalRef.current?.closeModal();
          successPublishPlaygroundModalRef.current?.openModal();
        }
      } else if (entityType === "challenge") {
        confirmPublishChallengeModalRef.current?.closeModal();
        successPublishChallengeModalRef.current?.openModal();
      }
    } catch (error) {
      showSnack(error, "error");
    } finally {
      setSavingState(false);
    }
  };

  const handleSaveProject = (newProjectName, lessonID, isAutoSave) => {
    const payload = {
      code: {
        python: isResetRef.current ? "" : python || "",
      },
      name: newProjectName || projectData?.name,
      is_submit: false,
      ...(lessonID && { lesson_id: lessonID }),
    };

    submitEntity({
      isAutoSave: isAutoSave,
      status: "save",
      entityType: "project",
      payload,
      isSubmit: false,
      setSavingState: setIsSaving,
      existingID: projectData?.id || projectID,
      serviceMethod: studentServices.saveOrPublishStudentProjects,
    });
    isResetRef.current = false;
  };
  const handlePublishProject = async (newProjectName, lessonID) => {
    const payload = {
      code: {
        python: python || "",
      },
      name: newProjectName || projectData?.name,
      is_submit: true,
      ...(lessonID && { lesson_id: lessonID }),
    };

    submitEntity({
      status: "publish",
      entityType: "project",
      payload,
      isSubmit: true,
      setSavingState: setIsPublishing,
      existingID: projectData?.id || projectID,
      serviceMethod: studentServices.saveOrPublishStudentProjects,
    });

    if (type == "graduation") {
      await lessonServices.finishLesson(lessonID);
    }
  };
  const handleSaveChallenge = (newChallengeName, isAutoSave) => {
    const payload = {
      code: {
        python: isResetRef.current ? "" : python || "",
      },
      name: newChallengeName || challengeData?.name,
      is_submit: false,
    };

    submitEntity({
      isAutoSave: isAutoSave,
      status: "save",
      entityType: "challenge",
      payload,
      isSubmit: false,
      setSavingState: setIsSaving,
      existingID: projectData?.lesson_project_id || projectData?.id || projectID,
      serviceMethod: studentServices.saveOrPublishStudentChallenges,
    });
    isResetRef.current = false;
  };
  const handlePublishChallenge = (newChallengeName) => {
    const payload = {
      code: {
        python: python || "",
      },
      name: newChallengeName || challengeData?.name,
      is_submit: true,
    };

    submitEntity({
      status: "publish",
      entityType: "challenge",
      payload,
      isSubmit: true,
      setSavingState: setIsPublishing,
      existingID: projectData?.lesson_project_id || projectData?.id || projectID,
      serviceMethod: studentServices.saveOrPublishStudentChallenges,
    });
  };

  const handleGetRandomChallengeProject = async () => {
    try {
      const response = await studentServices.getStudentRandomChallenge(lessonID, difficulty);
      setProjectData(response);
      setProjectID(response?.id);
      setProjectName(response?.name);

      challengeProjectGoalModalRef.current?.openModal(response);
    } catch (error) {
      showSnack(error, "error");
    } finally {
      console.log("Random challenge project loaded");
    }
  };

  const handleGetGraduationProject = async () => {
    try {
      const response = await studentServices.getGraduationProject(lessonID);
      const { id, ...rest } = response;
      setProjectData(rest);
      setProjectName(response?.name);
      setCodeData(response?.project?.code);
      setProjectID(response?.project?.id);
      challengeProjectGoalModalRef.current?.openModal(response);
    } catch (error) {
      showSnack(error, "error");
    }
  };

  const handleGetPlaygroundProject = async (payload) => {
    setPython("");

    if (payload?.difficulty) {
      try {
        const response = await lessonServices.getLessonProject(payload.lesson_project_id);
        setProjectData(response);
        challengeProjectGoalModalRef.current?.openModal(response);
      } catch (error) {
        console.error("Error fetching  data:", error);
      }
    } else {
      setProjectData(payload);
    }
    setCodeData(payload.code);
    setProjectName(payload?.name);
    setSubmitProjectStatus(payload);
    formik.setFieldValue("newProjectName", payload?.name);
  };

  const handleCreateNewProject = () => {
    setCodeData(null);
    setProjectData(null);
    setProjectID(null);
    setPython("");
    setSubmitProjectStatus(null);
    setProjectName(t("untitled_project"));
    formik.setFieldValue("newProjectName", t("untitled_project"));
  };

  useEffect(() => {
    if (type === "challenge" && difficulty) {
      handleGetRandomChallengeProject();
    } else if (type === "graduation") {
      handleGetGraduationProject();
    }
  }, []);

  const hasCodeContent = () => {
    return python?.trim() !== "";
  };

  const resetPythonCode = () => {
    setPython("");
    setCodeData(null);
    setReRunCode(!reRunCode);
    isResetRef.current = true; // Mark as reset

    if (type === "graduation") {
      handleSaveProject(projectName, lessonID, true);
    } else if (type === "challenge") {
      handleSaveChallenge(projectName, true);
    } else {
      handleSaveProject(projectName, null, true);
    }
  };

  const formik = useFormik({
    initialValues: {
      newProjectName: projectName,
    },
    validationSchema: Yup.object({
      newProjectName: Yup.string()
        .min(2, t("validation.field_min_2"))
        .max(25, t("validation.field_max_25"))
        .required(t("validation.required")),
    }),
    onSubmit: (values) => {
      setProjectName(values.newProjectName.trim());
      setRenameVisible(false);
    },
    enableReinitialize: true,
  });

  const footer = (
    <div className="flex justify-end gap-2 px-5 pb-3">
      <Button
        variant="outlined"
        color="white"
        onClick={() => {
          formik.resetForm();
          setRenameVisible(false);
        }}
        label={t("cancel")}
      />
      <Button
        className="w-full"
        type="submit"
        onClick={formik.handleSubmit}
        disabled={!formik.isValid || formik.isSubmitting}
        label={t("rename")}
      />
    </div>
  );

  return (
    <>
      {type == "code" ? (
        <h1 className="mb-2 w-full max-w-[33ch] truncate text-2xl font-bold text-blue-500">Lesson name</h1>
      ) : type == "challenge" || type == "graduation" ? (
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h1
              title={projectName}
              className="mb-2 w-full max-w-[33ch] truncate text-2xl font-bold text-blue-500"
            >
              {projectName}
            </h1>
            <span className="mb-2">
              {projectData?.last_status ||
                submitProjectStatus?.last_status_name ||
                submitProjectStatus?.last_status ||
                projectData?.project?.last_status}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="text"
              iconLeft={<MdOutlineFlag className="h-5 w-5" />}
              label={t("check_project_goal")}
              onClick={() => challengeProjectGoalModalRef.current?.openModal(projectData)}
              disabled={isSaving}
            />
            {!difficulty && type !== "graduation" && (
              <Button
                variant="text"
                iconLeft={<MdOutlineFolderOpen className="h-5 w-5" />}
                label={t("my_projects")}
                onClick={() => myProjectsModalRef.current?.openModal({ IDEType: IDE_TYPE.PYTHON })}
                disabled={isSaving}
              />
            )}
            <Button
              className="absolute left-6 top-12 z-50 h-10 w-36"
              iconLeft={<MdOutlineSave size={18} />}
              label={t("save_project")}
              color="green"
              loading={isSaving}
              onClick={
                type == "graduation"
                  ? () => handleSaveProject(projectName, lessonID)
                  : () => handleSaveChallenge(projectName)
              }
              disabled={!hasCodeContent() || isPublishing || isSaving}
            />
            <Button
              className="absolute left-[175px] top-12 z-50 h-10 w-36 !bg-[#11448F]"
              iconLeft={<PublishIcon className="h-4 w-4" />}
              label={t("publish")}
              onClick={
                type == "graduation"
                  ? () => confirmPublishGraduationModalRef.current?.openModal()
                  : () => confirmPublishChallengeModalRef.current?.openModal()
              }
              disabled={!hasCodeContent() || isPublishing || isSaving}
            />
          </div>
        </div>
      ) : (
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="flex cursor-pointer items-center space-x-2"
              onClick={() => setRenameVisible(true)}
              ref={popupRef}
            >
              <h1
                title={projectName}
                className="w-full max-w-[33ch] truncate text-2xl font-bold text-blue-500"
              >
                {projectName}
              </h1>
              <FaEdit className="!mx-1 h-5 w-5 text-blue-500" />
            </div>
            <span>
              {projectData?.last_status ||
                submitProjectStatus?.last_status_name ||
                submitProjectStatus?.last_status ||
                projectData?.project?.last_status}
            </span>
            <ConfirmPopup
              target={popupRef.current}
              visible={renameVisible}
              onHide={() => {
                setRenameVisible(false);
                formik.resetForm();
              }}
              message={
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-2">
                  <input
                    type="text"
                    name="newProjectName"
                    className={`w-full rounded border p-2 ${
                      formik.errors.newProjectName && formik.touched.newProjectName
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={formik.values.newProjectName}
                    maxLength={30}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.errors.newProjectName && formik.touched.newProjectName && (
                    <div className="text-sm text-red-500">{formik.errors.newProjectName}</div>
                  )}
                </form>
              }
              footer={footer}
              pt={{ message: "m-0" }}
            />
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="text"
              iconLeft={<MdOutlineFolderOpen className="h-5 w-5" />}
              label={t("my_projects")}
              loading={isSaving}
              onClick={() => myProjectsModalRef.current?.openModal({ IDEType: IDE_TYPE.PYTHON })}
              disabled={isSaving}
            />
            <Button
              className="absolute left-6 top-12 z-50 h-10 w-36"
              iconLeft={<MdOutlineSave size={18} />}
              label={t("save_project")}
              color="green"
              loading={isSaving}
              onClick={() => handleSaveProject(projectName)}
              disabled={!hasCodeContent() || isPublishing || isSaving}
            />
            <Button
              className="absolute left-[175px] top-12 z-50 h-10 w-36 !bg-[#11448F]"
              iconLeft={<PublishIcon className="h-4 w-4" />}
              label={t("publish")}
              onClick={() => confirmPublishPlaygroundModalRef.current?.openModal()}
              disabled={!hasCodeContent() || isPublishing || isSaving}
            />
          </div>
        </div>
      )}
      <PythonCodeEditor
        python={python}
        setPython={setPython}
        initialCode={rest?.codeData?.code || codeData}
        resetPythonCode={resetPythonCode}
        hasCodeContent={hasCodeContent}
        reRunCode={reRunCode}
      />
      {/* Modals */}
      <MyProjectsModal
        ref={myProjectsModalRef}
        onSubmit={handleGetPlaygroundProject}
        onSubmitCreate={handleCreateNewProject}
      />
      <ChallengeProjectGoalModal ref={challengeProjectGoalModalRef} />
      <ConfirmPublishChallengeModal
        isLoading={isPublishing}
        ref={confirmPublishChallengeModalRef}
        onSubmit={HandleConfirmPublishChallengeModalSuccess}
      />
      <ConfirmPublishGraduationModal
        isLoading={isPublishing}
        ref={confirmPublishGraduationModalRef}
        onSubmit={HandleConfirmPublishGraduationModalSuccess}
      />
      <ConfirmPublishPlaygroundModal
        isLoading={isPublishing}
        ref={confirmPublishPlaygroundModalRef}
        onSubmit={HandleConfirmPublishPlaygroundModalSuccess}
      />
      <SuccessPublishChallengeModal
        ref={successPublishChallengeModalRef}
        onSubmit={HandleSuccessPublishChallengeModalSuccess}
      />
      <SuccessPublishGraduationModal
        ref={successPublishGraduationModalRef}
        onSubmit={HandleSuccessPublishGraduationModalSuccess}
      />
      <SuccessPublishPlaygroundModal
        ref={successPublishPlaygroundModalRef}
        onSubmit={HandleSuccessPublishPlaygroundModalSuccess}
      />
    </>
  );
};

export default StudentPythonIDEWrapper;
